import { useState, useEffect } from 'react';
import { XMarkIcon, PlusIcon, DocumentDuplicateIcon, FunnelIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

export default function MaterialExtraPresetSelector({ onSelectPreset, onCreateNew, onCancel }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/material-extras', {
        params: { ownership: 'system' }
      });
      setPresets(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch presets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPreset = async (preset) => {
    setDuplicating(true);
    setError('');
    try {
      const response = await api.post(`/auth/material-extras/${preset._id}/duplicate?from=system`);
      onSelectPreset(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create from preset');
    } finally {
      setDuplicating(false);
    }
  };

  const getName = (preset) => {
    return typeof preset.name === 'object' ? preset.name.default : preset.name;
  };

  const filteredPresets = presets.filter(preset => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const name = getName(preset);
    return (
      name?.toLowerCase().includes(search) ||
      preset.key?.toLowerCase().includes(search)
    );
  });

  return (
    <div className="fixed inset-0 bg-slate-900 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Add Material Extra</h2>
            <p className="text-sm text-slate-500 mt-1">Select a preset or create from scratch</p>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2 mb-3">
            <FunnelIcon className="h-4 w-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Filter presets</span>
          </div>
          <input
            type="text"
            placeholder="Search by name or key..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="text-slate-500">Loading presets...</div>
            </div>
          ) : filteredPresets.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-slate-400 text-lg mb-2">No presets found</div>
              <p className="text-sm text-slate-500 mb-4">
                {searchTerm
                  ? 'Try adjusting your search or create a new material extra from scratch.'
                  : 'No system presets available. Create a new material extra from scratch.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredPresets.map((preset) => (
                <div
                  key={preset._id}
                  className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => !duplicating && handleSelectPreset(preset)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-slate-900 truncate group-hover:text-blue-600">
                        {getName(preset)}
                      </h3>
                      <div className="text-xs text-slate-500 mt-1">
                        <span className="font-mono bg-slate-100 px-1 rounded">{preset.key}</span>
                      </div>
                    </div>
                    <DocumentDuplicateIcon className="h-5 w-5 text-slate-300 group-hover:text-blue-500 flex-shrink-0" />
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs">
                    <span className="text-slate-400">
                      Order: {preset.displayOrder || 0}
                    </span>
                    <span className="text-slate-500 font-medium">
                      {(preset.materialCostMultiplier || 1).toFixed(2)}x cost
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-500">
            {!loading && `${filteredPresets.length} preset${filteredPresets.length !== 1 ? 's' : ''} available`}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={duplicating}
              className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onCreateNew}
              disabled={duplicating}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg disabled:opacity-50 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Create from Scratch
            </button>
          </div>
        </div>

        {/* Loading overlay */}
        {duplicating && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
            <div className="text-slate-600 font-medium">Creating material extra...</div>
          </div>
        )}
      </div>
    </div>
  );
}
